import {useCallback, useEffect, useRef, useState} from 'react';
import {patientService} from '../../services/patientService';

const EMPTY_STATE: Record<string, any> = {
  sessions: [],
  isLoading: false,
  error: null,
  hasLoaded: false,
};

export default function usePlannerRemovedSessions({
  userId,
  enabled = true,
  startDate = null,
  endDate = null,
  refreshKey = 0,
}: Record<string, any> = {}) {
  const [state, setState] = useState(EMPTY_STATE);
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    if (!enabled || !userId) {
      setState(EMPTY_STATE);
      return [];
    }

    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      // Schema V2: the caller's own soft-deleted sessions come from
      // GET /api/v2/sessions/removed (server-side providerUserId + isRemoved
      // filter), already mapped to the legacy Conversation shape.
      const sessions =
        (await patientService.getRemovedSessions({
          limit: 100,
          startDate,
          endDate,
        })) || [];
      if (requestIdRef.current !== requestId) return [];
      setState({
        sessions,
        isLoading: false,
        error: null,
        hasLoaded: true,
      });
      return sessions;
    } catch (err) {
      if (requestIdRef.current !== requestId) return [];
      console.error('Error loading planner removed sessions:', err);
      setState({
        ...EMPTY_STATE,
        isLoading: false,
        error: err,
        hasLoaded: true,
      });
      return [];
    }
  }, [enabled, endDate, refreshKey, startDate, userId]);

  useEffect(() => {
    refresh();
    return () => {
      requestIdRef.current += 1;
    };
  }, [refresh]);

  const setSessions = useCallback((updater: any) => {
    setState(prev => ({
      ...prev,
      sessions:
        typeof updater === 'function' ? updater(prev.sessions) : updater || [],
    }));
  }, []);

  return {
    sessions: state.sessions,
    isLoading: state.isLoading,
    error: state.error,
    hasLoaded: state.hasLoaded,
    refresh,
    setSessions,
  };
}
